import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import apiClient from '../utils/apiClient';
import {
  getAvailableEnvironments,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  getApiEndpoints,
  ENVIRONMENT_LABELS,
  getEnvironmentColor
} from '../utils/apiConfig';
import './SetupAgentPage.css'; // reuse existing styles — no new CSS

const SetupProgramDataPage = () => {
  const [loading, setLoading]   = useState(false);
  const [result, setResult]     = useState(null);
  const [error, setError]       = useState(null);
  const [csvFile, setCsvFile]   = useState(null);
  const [uploadStep, setUploadStep] = useState('');
  const submittingRef = useRef(false); // guard against StrictMode double-invoke // status message during multi-step upload

  // Environment selection
  const [selectedEnvironment, setSelectedEnvironment] = useState(getSelectedEnvironment());
  const availableEnvironments = getAvailableEnvironments();

  const apiEndpoints        = getApiEndpoints(selectedEnvironment);
  const PROGRAM_DATA_API        = apiEndpoints.programDataApi;
  const PROGRAM_DATA_PRESIGN_API = `${apiEndpoints.bulkAgentPresignApi}?type=program-data`;

  const handleEnvironmentChange = (e) => {
    const newEnv = e.target.value;
    setSelectedEnvironment(newEnv);
    saveSelectedEnvironment(newEnv);
    setResult(null);
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.csv')) {
      setError('Please select a .csv file');
      setCsvFile(null);
      return;
    }
    setError(null);
    setResult(null);
    setCsvFile(file);
  };

  const handleDownloadSample = () => {
    const link = document.createElement('a');
    link.href = `${process.env.PUBLIC_URL}/sample_program_data_upload.csv`;
    link.download = 'sample_program_data_upload.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      setError('Please select a CSV file to upload');
      return;
    }
    if (submittingRef.current) return; // prevent double-submit (React StrictMode / rapid clicks)
    submittingRef.current = true;

    setLoading(true);
    setError(null);
    setResult(null);
    setUploadStep('');

    try {
      // Step 1 — get a pre-signed S3 PUT URL
      setUploadStep('Requesting upload URL…');
      const presignResp = await apiClient.get(PROGRAM_DATA_PRESIGN_API);
      const { presign_url, s3_key, job_id } = presignResp.data;

      // Step 2 — PUT the CSV file directly to S3 (bypasses API Gateway / WAF)
      setUploadStep('Uploading file to S3…');
      await axios.put(presign_url, csvFile, {
        headers: { 'Content-Type': 'text/csv' },
        // Prevent axios from serialising the File object or adding extra headers
        transformRequest: [(data) => data],
      });

      // Step 3 — notify the backend to queue processing
      setUploadStep('Queuing processing job…');
      const response = await apiClient.post(PROGRAM_DATA_API, { job_id, s3_key });

      let bodyData = response.data;
      if (bodyData.body && typeof bodyData.body === 'string') {
        try { bodyData = JSON.parse(bodyData.body); } catch (_) {}
      }

      setResult({
        job_id:  bodyData.job_id  || job_id,
        message: bodyData.message || 'File accepted and queued for processing.',
        s3_key:  bodyData.s3_key  || s3_key,
      });

    } catch (err) {
      console.error('Program data upload error:', err);
      // Log S3 XML error body if present (helps diagnose presigned URL 403s)
      if (err.response) {
        console.error('S3/API error status:', err.response.status);
        console.error('S3/API error data:', err.response.data);
      }
      const errorData = err.response?.data;
      if (errorData?.body && typeof errorData.body === 'string') {
        try {
          const parsed = JSON.parse(errorData.body);
          setError(parsed.message || parsed.error || 'Upload failed');
        } catch (_) {
          setError(errorData.body || 'Upload failed');
        }
      } else {
        setError(
          errorData?.message || errorData?.error || err.message || 'Upload failed'
        );
      }
    } finally {
      setLoading(false);
      setUploadStep('');
      submittingRef.current = false;
    }
  };

  return (
    <div className="setup-page">
      <div className="page-header">
        <div>
          <h1>Setup Colleague Data</h1>
          <p className="page-subtitle">Upload a CSV file to add or update colleague records in the program data table</p>
        </div>
        <Link to="/home" className="btn btn-secondary">← Back to Home</Link>
      </div>

      {/* Environment selector */}
      {availableEnvironments.length > 1 && (
        <div className="environment-selector">
          <label htmlFor="env-select">Environment:</label>
          <select
            id="env-select"
            value={selectedEnvironment}
            onChange={handleEnvironmentChange}
            className="env-select"
            style={{ borderColor: getEnvironmentColor(selectedEnvironment) }}
          >
            {availableEnvironments.map((env) => (
              <option key={env} value={env}>
                {ENVIRONMENT_LABELS[env] || env}
              </option>
            ))}
          </select>
          <span
            className="env-badge"
            style={{ backgroundColor: getEnvironmentColor(selectedEnvironment) }}
          >
            {ENVIRONMENT_LABELS[selectedEnvironment] || selectedEnvironment}
          </span>
        </div>
      )}

      <div className="setup-section">
        {/* CSV format info card */}
        <div className="info-box" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem', backgroundColor: '#f3f0ff', border: '1px solid #c4b5fd', borderRadius: '8px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '0.5rem', color: '#673ab7' }}>📋 CSV File Format</h3>
          <p style={{ margin: '0 0 0.5rem' }}>The CSV must include a header row with the following columns:</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', fontSize: '0.85rem', width: '100%' }}>
              <thead>
                <tr style={{ backgroundColor: '#673ab7', color: '#fff' }}>
                  <th style={{ padding: '6px 10px', textAlign: 'left' }}>Column</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left' }}>Required</th>
                  <th style={{ padding: '6px 10px', textAlign: 'left' }}>Description</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['EEID',             '✅ Yes', 'Unique employee ID — used as the record key'],
                  ['Program',          '✅ Yes', 'Program name (e.g. GLOBALCORE)'],
                  ['First',            'No',     'First name'],
                  ['Last',             'No',     'Last name'],
                  ['Colleague Name',   'No',     'Full name (informational)'],
                  ['StartDateStr',     'No',     'Program start date (MM/DD/YYYY)'],
                  ['EndDateStr',       'No',     'Program end date (MM/DD/YYYY)'],
                  ['ModifyBy',         'No',     'Name of person making the change'],
                  ['ModifyDateTimeStr','No',     'Date/time of modification'],
                  ['ModifyReason',     'No',     'Reason for the change'],
                  ['Notes',            'No',     'Additional notes'],
                ].map(([col, req, desc]) => (
                  <tr key={col} style={{ borderBottom: '1px solid #e9d5ff' }}>
                    <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontWeight: '600' }}>{col}</td>
                    <td style={{ padding: '5px 10px' }}>{req}</td>
                    <td style={{ padding: '5px 10px', color: '#555' }}>{desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '0.75rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={handleDownloadSample}
              style={{
                backgroundColor: '#673ab7', color: '#fff', border: 'none',
                padding: '0.5rem 1.25rem', borderRadius: '4px', cursor: 'pointer',
                fontSize: '0.9rem', fontWeight: '500',
              }}
            >
              ⬇ Download Sample CSV
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-group">
            <label htmlFor="csv-file">Colleague Data CSV File *</label>
            <input
              id="csv-file"
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileChange}
              disabled={loading}
              className="form-control file-input"
            />
            <p className="form-hint">
              Upload a CSV file containing colleague records. Each row must include{' '}
              <code>EEID</code> and <code>Program</code>. Existing records with the same
              EEID will be overwritten.
            </p>
            {csvFile && (
              <p className="form-hint" style={{ color: '#4caf50' }}>
                ✓ Selected: <strong>{csvFile.name}</strong> ({(csvFile.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          {error && (
            <div className="alert alert-error">
              <strong>Error:</strong> {error}
            </div>
          )}

          {loading && uploadStep && (
            <div className="alert alert-info">
              <span className="spinner-inline" /> {uploadStep}
            </div>
          )}

          {result && (
            <div className="alert alert-success">
              <h4>✅ Colleague Data Upload Submitted</h4>
              <p>{result.message}</p>
              <p style={{ fontSize: '0.85rem', color: '#555', marginBottom: '0.75rem' }}>
                Job ID: <code>{result.job_id}</code>
              </p>
              <p>
                The file is being processed in the background. Colleague records will be
                upserted into the program data table shortly.
              </p>
              <Link to="/bulk-jobs" className="btn btn-primary" style={{ display: 'inline-block' }}>
                View Job Status →
              </Link>
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !csvFile}
            >
              {loading ? 'Uploading…' : 'Upload Data'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={loading}
              onClick={() => {
                setCsvFile(null);
                setResult(null);
                setError(null);
                document.getElementById('csv-file').value = '';
              }}
            >
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SetupProgramDataPage;
